import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, Activity, FileText, CheckCircle2, XCircle, Clock } from "lucide-react";

/**
 * ContentHealthOverview — at-a-glance metrics for the content pipeline.
 * Drops into the existing SocialMediaManager as an overview/dashboard panel.
 */
export function ContentHealthOverview() {
  const { data: stats } = useQuery({
    queryKey: ["content_health_overview"],
    queryFn: async () => {
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const week = new Date(); week.setDate(week.getDate() - 7);
      const month = new Date(); month.setDate(month.getDate() - 30);

      const all = await supabase.from("social_posts" as any).select("status, created_at, approved_at, posted_at, platform, content_type, campaign_id");
      const rows = (all.data ?? []) as Array<any>;

      const total = rows.length;
      const pending = rows.filter(r => r.status === 'pending_approval').length;
      const approved = rows.filter(r => r.status === 'approved').length;
      const denied = rows.filter(r => r.status === 'denied').length;
      const posted = rows.filter(r => r.status === 'posted').length;

      const approvedThisWeek = rows.filter(r => r.approved_at && new Date(r.approved_at) >= week).length;
      const approvedThisMonth = rows.filter(r => r.approved_at && new Date(r.approved_at) >= month).length;
      const postedThisWeek = rows.filter(r => r.posted_at && new Date(r.posted_at) >= week).length;
      const createdThisWeek = rows.filter(r => r.created_at && new Date(r.created_at) >= week).length;

      const denialRate = (approved + denied) === 0 ? 0 : Math.round(100 * denied / (approved + denied));

      // Per-platform breakdown
      const byPlatform: Record<string, { total: number; pending: number; approved: number; posted: number }> = {};
      rows.forEach(r => {
        const p = r.platform || 'unknown';
        if (!byPlatform[p]) byPlatform[p] = { total: 0, pending: 0, approved: 0, posted: 0 };
        byPlatform[p].total++;
        if (r.status === 'pending_approval') byPlatform[p].pending++;
        if (r.status === 'approved') byPlatform[p].approved++;
        if (r.status === 'posted') byPlatform[p].posted++;
      });

      // Per-campaign + content_type breakdown for top 10
      const byCampaignType: Record<string, number> = {};
      rows.filter(r => r.status === 'pending_approval').forEach(r => {
        const k = r.content_type ?? 'unknown';
        byCampaignType[k] = (byCampaignType[k] ?? 0) + 1;
      });
      const topContentTypes = Object.entries(byCampaignType)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10);

      return {
        total, pending, approved, denied, posted,
        approvedThisWeek, approvedThisMonth, postedThisWeek, createdThisWeek,
        denialRate, byPlatform, topContentTypes,
      };
    },
    refetchInterval: 60_000,
  });

  if (!stats) {
    return <div className="p-4 text-sm text-muted-foreground">Loading content health…</div>;
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Pending Review</span>
              <Clock className="w-3.5 h-3.5 text-yellow-600" />
            </div>
            <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
            <div className="text-xs text-muted-foreground">awaiting your approval</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Approved (queue)</span>
              <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
            </div>
            <div className="text-2xl font-bold text-green-600">{stats.approved}</div>
            <div className="text-xs text-muted-foreground">{stats.approvedThisWeek} this week · {stats.approvedThisMonth} this month</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Published</span>
              <Activity className="w-3.5 h-3.5 text-blue-600" />
            </div>
            <div className="text-2xl font-bold text-blue-600">{stats.posted}</div>
            <div className="text-xs text-muted-foreground">{stats.postedThisWeek} this week</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Denial Rate</span>
              {stats.denialRate >= 30
                ? <TrendingUp className="w-3.5 h-3.5 text-red-600" />
                : <TrendingDown className="w-3.5 h-3.5 text-green-600" />}
            </div>
            <div className="text-2xl font-bold">{stats.denialRate}%</div>
            <div className="text-xs text-muted-foreground">{stats.denied} denied / {stats.approved + stats.denied} reviewed</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <FileText className="w-4 h-4" /> Pending content by platform
          </CardTitle>
          <CardDescription className="text-xs">
            What's queued up for review, broken down by where it goes.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-2">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {Object.entries(stats.byPlatform)
              .filter(([, v]) => v.pending > 0)
              .sort((a, b) => b[1].pending - a[1].pending)
              .map(([platform, v]) => (
                <div key={platform} className="border rounded-md p-2 text-xs">
                  <div className="font-semibold capitalize">{platform.replace('_', ' ')}</div>
                  <div className="text-yellow-600">{v.pending} pending</div>
                  {v.approved > 0 && <div className="text-green-600">{v.approved} approved</div>}
                  {v.posted > 0 && <div className="text-blue-600">{v.posted} posted</div>}
                </div>
              ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Top pending content types</CardTitle>
          <CardDescription className="text-xs">
            What kinds of drafts are waiting on you. Tip: you can bulk-approve by content type in the Approval tab.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-2">
          <div className="flex flex-wrap gap-2">
            {stats.topContentTypes.map(([type, count]) => (
              <Badge key={type} variant="outline" className="text-xs">
                {type} <span className="ml-1 font-bold">{count}</span>
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
